import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { zapPaths } from '../../zapPaths';
import { Card, CardContent } from '../../../../../../components/tailwind-component/ui/card';
import { Button } from '../../../../../../components/tailwind-component/ui/button';
import { Input } from '../../../../../../components/tailwind-component/ui/input';
import { Label } from '../../../../../../components/tailwind-component/ui/label';
import { Textarea } from '../../../../../../components/tailwind-component/ui/textarea';
import { Badge } from '../../../../../../components/tailwind-component/ui/badge';
import { Switch } from '../../../../../../components/tailwind-component/ui/switch';
import { Plus, Trash2, MessageSquare, Send, Hash, X, Shuffle, Pencil, Zap, Info, Lock, MousePointerClick, UserPlus, Link2, FileText } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import { Tooltip as MaterialTooltip } from '@mui/material'
// import { toast } from 'sonner';
import { toast } from 'react-toastify';

import { AutomationRuleEntry, AutomationContentType, PlanEntitlements, ZapLinkProduct } from 'custom-type';
import { ZapLinkSection } from './ZapLinkSection';
import { useNavigate } from 'react-router-dom'
import { InstagramDMAutomation } from '../../../../../../core/services/dm-automation-instagram.service';

import { IconButton } from '@mui/material';
import MaterialSymbol from '../../../../../../components/icons/material-symbol';
import useBodyClass from '../../../../../../hooks/useBodyClass';
import { limitAsNumber, formatEntitlementLimit, isFollowGateAllowed } from '../../../../../../core/utility';

// Matches ZapLinkSection: the service exposes instance methods, not statics.
const zapCtaService = new InstagramDMAutomation();
// import { AutomationContentType } from '../SetupWizard';

interface StepConfigureRulesProps {
  name: string;
  setName: (name: string) => void;

  rules: AutomationRuleEntry[];
  setRules: (rules: AutomationRuleEntry[]) => void;
  contentType: AutomationContentType;

  entitlements: PlanEntitlements | null;
  /** Owner ids used to look up the creator's storefront / link-in-bio for the Zap Link CTA. */
  ctaOwner?: { influencer_id: number | string; account_id: number | string };
  /** Leaves the wizard and lands on the subscription screen (used by the follow-gate lock). */
  handleGoToUpgrade: () => void;
  handleOpenUpgradePopup: (upgradeContent: React.ReactNode) => void;
  onClose: () => void;
  currentPlanName: string | null;
  allowOtherSource: boolean;
  zapProductImageUploadPath: string;
}

/* ---------------------------------------------------------------------------
   STARTER COPY

   Wording lifted from the creators we studied (upskill_sahil, austinnasso,
   ankit_purohit_japan, saurabhs.builds, engineerhoon_2.0) rather than invented,
   so the defaults are things that demonstrably work on Instagram.

   A chip carries a SHORT name and fills in the LONG text, so a row of choices
   stays one line high no matter how long the copy behind it is. Chips hide as
   soon as the creator writes something of their own — see SuggestionChips —
   which is what keeps the form from growing for anyone who does not need them.
   --------------------------------------------------------------------------- */
type CopyPreset = { name: string; text: string };

const OPENER_MESSAGE_PRESETS: CopyPreset[] = [
  { name: 'Friendly', text: "Hi there! Thanks for your interest 😊 Tap below and I'll send the link right over ✨" },
  { name: 'Warm', text: "Hey! I'm so glad you're here — thanks a ton for stopping by 😊\n\nTap below and I'll send you the access in just a moment ✨" },
  { name: 'Short', text: "Tap below and I'll send it over 👇" },
  { name: 'Made for you', text: "I put this together especially for you. Tap below and it's yours." },
];

const OPENER_BUTTON_PRESETS = ['Send me the link', 'Send Link Now', 'Yes, send it over', 'Send me the access'];

const FOLLOW_ASK_PRESETS: CopyPreset[] = [
  { name: 'Polite', text: "Thanks for your comment! 😊 Make sure you're following me so I can send you the link." },
  { name: 'Direct', text: "Almost there — follow me and tap below, and I'll send it straight over." },
];

const DM_MESSAGE_PRESETS: CopyPreset[] = [
  { name: 'As promised', text: "Hi there! Appreciate your comment 🙌 As promised, here's the link for you ⬇️" },
  { name: 'Here you go', text: "Here you go! Tap the button below to open it." },
  { name: 'Thanks', text: "Thanks for waiting — here's exactly what you asked for 👇" },
];

const LINK_BUTTON_PRESETS = ['Get the link', 'Download now', 'Get access', 'Join here'];

/**
 * A one-line row of starter copy. Clicking a chip fills the field.
 *
 * Visible only while the field is empty OR still holds one of the presets
 * verbatim — so it doubles as a way to cycle between them, then gets out of the
 * way permanently the moment the creator edits the text. That is what keeps
 * this from adding height to a form someone has already filled in.
 */
function SuggestionChips({ presets, value, onPick }: {
  presets: CopyPreset[] | string[];
  value: string;
  onPick: (text: string) => void;
}) {
  const items: CopyPreset[] = (presets as any[]).map((p) =>
    typeof p === 'string' ? { name: p, text: p } : p
  );

  const trimmed = (value || '').trim();
  const matchesPreset = items.some((i) => i.text === trimmed);
  if (trimmed !== '' && !matchesPreset) return null;

  return (
    <div className="flex gap-1.5 flex-wrap items-center">
      <span className="text-[11px] text-gray-400 leading-none">Try:</span>
      {items.map((item) => {
        const active = item.text === trimmed;
        return (
          <button
            type="button"
            key={item.name}
            title={item.text}
            onClick={() => onPick(active ? '' : item.text)}
            className={`text-[11px] leading-none px-2 py-[4px] rounded-full border transition-colors ${
              active
                ? 'bg-[#f3f0ff] border-[#c4b5fd] text-[#7c3aed]'
                : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
          >
            {item.name}
          </button>
        );
      })}
    </div>
  );
}

/**
 * What the wizard footer needs to know before it submits.
 *
 * The keyword / comment-reply fields and the whole rule form are STAGING state:
 * nothing reaches `rules` until the + icon or Add/Update Rule is pressed. A
 * creator who types into a field and goes straight for Create Automation used to
 * lose that text silently. The footer now asks the step to settle itself first.
 */
export type StepConfigureRulesHandle = {
  /** Commits text typed into a "+" field but never added. No-op when the fields are empty, so it is safe to call on every submit. */
  flushPendingDrafts: () => void;
  /** true while the rule form is open — i.e. keywords/replies/messages exist that no rule owns yet. */
  hasOpenRule: () => boolean;
  /** Runs the exact validation the Add/Update Rule button runs. false means it refused and has already explained why. */
  commitOpenRule: () => boolean;
  /** Throws the open rule form away. Never touches rules that are already saved. */
  discardOpenRule: () => void;
};

export const StepConfigureRules = forwardRef<StepConfigureRulesHandle, StepConfigureRulesProps>(function StepConfigureRules({ name, setName, rules, setRules, contentType, entitlements, ctaOwner, handleOpenUpgradePopup, handleGoToUpgrade, onClose, currentPlanName, allowOtherSource, zapProductImageUploadPath }: StepConfigureRulesProps, ref) {
  const [keyword, setKeyword] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [commentReply, setCommentReply] = useState('');
  const [commentReplies, setCommentReplies] = useState<string[]>([]);
  const [randomReply, setRandomReply] = useState(true);
  const [dmMessage, setDmMessage] = useState('');
  const [isAddingRule, setIsAddingRule] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'auto_reply' | 'zap_link'>('auto_reply');
  const [zapLinks, setZapLinks] = useState<ZapLinkProduct[]>([]);

  /* DM FLOW — paid/trial only. 'instant' keeps the original behaviour: the DM
     goes out the moment they comment.

     OLD (pre 2026-09-01): the opener card was only reachable by turning the
     follow gate on, so a creator who just wanted a tappable button had to also
     force a follow. These are two separate decisions and the form now asks them
     as two, nested, because the follow check rides on the opener's button tap.
     const [followGateMode, setFollowGateMode] = useState<'off' | 'soft' | 'hard'>('off');
     const [followGateMessage, setFollowGateMessage] = useState('');
     const [followGateButtonLabel, setFollowGateButtonLabel] = useState(''); */
  const [dmFlowMode, setDmFlowMode] = useState<'instant' | 'button' | 'soft_follow' | 'hard_follow'>('instant');
  const [openerMessage, setOpenerMessage] = useState('');
  const [openerButtonLabel, setOpenerButtonLabel] = useState('');
  const [followAskMessage, setFollowAskMessage] = useState('');

  /* LINK BUTTON on the delivered DM. This is the piece every creator we studied
     uses and we had no way to express — they never deliver a bare URL, they
     deliver a card whose button is labelled with the offer. */
  const [messageButtonLabel, setMessageButtonLabel] = useState('');
  const [messageButtonUrl, setMessageButtonUrl] = useState('');

  /* SECOND BUTTON on every Zap Link card — the creator-level CTA that Wishlink-style
     carousels pair with each product's own button ("Shop My Looks"). Stored once per
     rule, not per product: it is the same button on every card. */
  const [zapCtaLabel, setZapCtaLabel] = useState('');
  const [zapCtaUrl, setZapCtaUrl] = useState('');
  /* Meta allows three buttons per card: the product link plus these two. The
     third is revealed only once the second is complete — buttons render in
     order, so an empty second slot would silently promote the third. */
  const [zapCta2Label, setZapCta2Label] = useState('');
  const [zapCta2Url, setZapCta2Url] = useState('');
  const [showZapCta2, setShowZapCta2] = useState(false);
  const [ctaSuggestions, setCtaSuggestions] = useState<Array<{ type: string; label: string; name: string; url: string }>>([]);
  const zapCtaUrlInvalid = zapCtaUrl.trim() !== '' && !/^https?:\/\//i.test(zapCtaUrl.trim());
  const zapCta2UrlInvalid = zapCta2Url.trim() !== '' && !/^https?:\/\//i.test(zapCta2Url.trim());
  const zapCtaComplete = zapCtaLabel.trim() !== '' && zapCtaUrl.trim() !== '' && !zapCtaUrlInvalid;

  // The same entitlement covers every button template: opener cards, the link
  // button, and the follow check. Key name kept from when it only meant the gate.
  const canUseFollowGate = isFollowGateAllowed(entitlements);
  /* The stored mode is one column of four values, because the four outcomes are
     genuinely exclusive. The FORM asks it as two questions instead — "send a
     button first?" and, only then, "check the follow?" — because that is how
     creators think about it, and because the follow check physically rides on
     the opener's button tap. These two map the one onto the other. */
  const openerEnabled = dmFlowMode !== 'instant';
  const followMode: 'none' | 'soft_follow' | 'hard_follow' =
    dmFlowMode === 'soft_follow' || dmFlowMode === 'hard_follow' ? dmFlowMode : 'none';

  const handleToggleOpener = (on: boolean) => {
    if (!canUseFollowGate) { handleGoToUpgrade(); return; }
    // Turning the opener off drops any follow requirement with it — a follow can
    // only be checked on a button tap, so 'instant' is the only coherent result.
    setDmFlowMode(on ? 'button' : 'instant');
  };

  const handleChangeFollowMode = (value: 'none' | 'soft_follow' | 'hard_follow') => {
    setDmFlowMode(value === 'none' ? 'button' : value);
  };

  const hasMessageButton = messageButtonLabel.trim() !== '' || messageButtonUrl.trim() !== '';
  /* Meta caps a button-template body at 640 chars, where a plain DM allows 1000.
     postIgButtonTemplate substrings to 640, so without this the tail of a long
     message would vanish the moment a button was added. */
  const dmCharLimit = hasMessageButton ? 640 : 1000;
  const messageButtonUrlInvalid =
    messageButtonUrl.trim() !== '' && !/^https?:\/\//i.test(messageButtonUrl.trim());
  const [keywordError, setKeywordError] = useState<React.ReactNode>(null);
  const [CommentRepliesError, setCommentRepliesError] = useState<React.ReactNode>(null);
  const [ruleError, setRuleError] = useState<React.ReactNode>(null);
  // Rule ids whose Zap Link product list is expanded. Collapsed by default so a
  // 10-product carousel does not bury the rest of the rule card.
  const [expandedZapRules, setExpandedZapRules] = useState<number[]>([]);
  const ctaSuggestionsLoaded = useRef(false);
  const formRef = useRef<HTMLDivElement>(null);
  const isStory = contentType === 'story';

  /* Loaded lazily and once: a creator who never opens the Zap Link tab should not
     pay for this request, and the answer cannot change mid-wizard. A failure is
     silent on purpose — the custom-URL fields still work without suggestions. */
  useEffect(() => {
    if (activeTab !== 'zap_link' || ctaSuggestionsLoaded.current) return;
    if (!ctaOwner?.influencer_id || !ctaOwner?.account_id) return;
    ctaSuggestionsLoaded.current = true;
    zapCtaService.getZapCtaSuggestions({
      influencer_id: ctaOwner.influencer_id,
      account_id: ctaOwner.account_id,
    })
      .then((res: any) => setCtaSuggestions(res?.data?.suggestions || []))
      .catch(() => setCtaSuggestions([]));
  }, [activeTab, ctaOwner]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  /* ---------------- ENTITLEMENT LIMITS ---------------- */

  // OLD (pre-unlimited): the raw entitlement value was used directly, so a -1
  // ("Unlimited") sentinel would have read as a cap of minus one and blocked
  // everything. limitAsNumber() maps -1 -> Infinity so the existing `<`/`>`
  // maths below keeps working untouched.
  // const maxKeywords = entitlements?.max_keywords_per_rule ?? Infinity;
  // const maxRules = entitlements?.max_dm_rules ?? Infinity;
  // const maxCommentRules = entitlements?.max_comment_rules ?? Infinity;
  const maxKeywords =
    limitAsNumber(entitlements?.max_keywords_per_rule, Infinity);

  const maxRules =
    limitAsNumber(entitlements?.max_dm_rules, Infinity);

  const maxCommentRules =
    limitAsNumber(entitlements?.max_comment_rules, Infinity);

  const allowedContentTypes =
    entitlements?.allowed_content_types ?? [];

  const brandingRequired =
    entitlements?.auto_dm_branding_required ?? false;

  // OLD (pre-unlimited): const maxZapProducts = entitlements?.zap_link_limit ?? 0;
  // 0 still means "Zap Links not available on this plan" (FOC); -1 -> Infinity.
  const maxZapProducts =
    limitAsNumber(entitlements?.zap_link_limit, 0);

  /* ---------------- END OF ENTITLEMENT LIMITS ---------------- */

  // ----------------------------
  // KEYWORD LOGIC
  // ----------------------------

  // const handleAddKeyword = () => {
  //   const trimmedKeyword = keyword.trim().toUpperCase();
  //   if (!trimmedKeyword) {
  //     toast.error('Please enter a keyword');
  //     return;
  //   }
  //   if (keywords.includes(trimmedKeyword)) {
  //     toast.error('Keyword already added');
  //     return;
  //   }
  //   setKeywords([...keywords, trimmedKeyword]);
  //   setKeyword('');
  // };

  const handleAddKeyword = () => {

    if (!keyword.trim()) {
      toast.error('Please enter a keyword');
      return;
    }

    /* Split by comma */
    const splitKeywords = keyword
      .split(",")
      .map(k => k.trim().toUpperCase())
      .filter(k => k !== "");

    if (splitKeywords.length === 0) {
      toast.error('Please enter a valid keyword');
      return;
    }

    /* ✅ Remove duplicates within same input */
    const uniqueInputKeywords = [...new Set(splitKeywords)];

    /* Remove duplicates vs existing keywords */
    const newUniqueKeywords = uniqueInputKeywords.filter(
      k => !keywords.includes(k)
    );

    if (newUniqueKeywords.length === 0) {
      toast.error('Keyword already added');
      return;
    }

    /* Check entitlement limit */

    // onClose();
    // handleOpenUpgradePopup(
    //   `You can add only ${maxKeywords} keywords in your current plan.`
    // );
    if (keywords.length + newUniqueKeywords.length > maxKeywords) {
      setKeywordError(
        <>
          You can add only {formatEntitlementLimit(entitlements?.max_keywords_per_rule)} keyword
          {maxKeywords > 1 ? "s" : ""} in your current plan.{" "}
          <span
            className="underline text-blue-600 cursor-pointer hover:text-blue-800"
            onClick={() => {
              onClose();
              navigate(zapPaths().subscription);
            }}
          >
            Upgrade
          </span>.
        </>
      );
      return;
    }
    // handleOpenUpgradePopup(
    //   <>
    //     You can have up to {maxKeywords} active automations. 
    //     To add more, please delete or pause an existing automation, or{" "}
    //     <span
    //       className="underline text-blue-600 cursor-pointer hover:text-blue-800"
    //       onClick={() => navigate(zapPaths().subscription)}
    //     >
    //       upgrade 
    //     </span> your plan.
    //   </>
    // );

    /* Add keywords */
    setKeywords([...keywords, ...newUniqueKeywords]);
    setKeyword("");
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    setKeywords(keywords.filter(k => k !== keywordToRemove));
  };

  // ----------------------------
  // REPLY LOGIC
  // ----------------------------

  const handleAddCommentReply = () => {
    if (commentReplies.length >= maxCommentRules) {
      // onClose();
      // handleOpenUpgradePopup(
      //   `You can add only ${maxCommentRules} comment replies in your current plan.`
      // );
      // return;
      setCommentRepliesError(
        <>
          You can add only {formatEntitlementLimit(entitlements?.max_comment_rules)} comment
          {maxCommentRules > 1 ? "replies" : "reply"} in your current plan.{" "}
          <span
            className="underline text-blue-600 cursor-pointer hover:text-blue-800"
            onClick={() => {
              onClose();
              navigate(zapPaths().subscription);
            }}
          >
            Upgrade
          </span>.
        </>
      );
      return;
    }
    const trimmedReply = commentReply.trim();
    if (!trimmedReply) {
      toast.error('Please enter a comment reply');
      return;
    }
    setCommentReplies([...commentReplies, trimmedReply]);
    setCommentReply('');
  };

  const handleRemoveCommentReply = (index: number) => {
    setCommentReplies(commentReplies.filter((_, i) => i !== index));
    // If we have less than 2 replies, disable random mode
    if (commentReplies.length <= 2) {
      setRandomReply(true);
    }
  };

  // ----------------------------
  // RULE CREATION / EDIT
  // ----------------------------

  const handleEditRule = (rule: AutomationRuleEntry) => {
    setKeywords(rule.keywords || []);
    setCommentReplies(rule.replies || []);
    setRandomReply(rule.random_reply || true);
    setDmMessage(rule.message_text || '');
    setEditingRuleId(rule?.ig_automation_rule_id || null);
    setActiveTab(rule.rule_type || 'auto_reply');
    setZapLinks(rule.zap_links || []);
    setZapCtaLabel(rule.zap_cta_label || '');
    setZapCtaUrl(rule.zap_cta_url || '');
    setZapCta2Label(rule.zap_cta2_label || '');
    setZapCta2Url(rule.zap_cta2_url || '');
    setShowZapCta2(Boolean(rule.zap_cta2_label || rule.zap_cta2_url));
    setDmFlowMode(rule.dm_flow_mode || 'instant');
    setOpenerMessage(rule.opener_message || '');
    setOpenerButtonLabel(rule.opener_button_label || '');
    setFollowAskMessage(rule.follow_ask_message || '');
    setMessageButtonLabel(rule.button_label || '');
    setMessageButtonUrl(rule.button_url || '');
    setIsAddingRule(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  };

  /* Returns TRUE only when the rule actually reached `rules`. Every guard below
     already explains itself with a toast/inline error, so the caller only needs
     to know whether it may carry on. The Add/Update Rule button ignores the
     value; the "unsaved rule" gate in SetupWizard does not. */
  const handleAddRule = (): boolean => {
    // if (editingRuleId === null && rules.length >= maxRules + maxCommentRules) {
    //   onClose();
    //   handleOpenUpgradePopup(
    //     `You have reached the maximum number of rules for your current plan.`
    //   );
    //   return;
    // }

    if (editingRuleId === null && rules.length >= maxRules + maxCommentRules) {
      setRuleError(
        // OLD (pre-unlimited): `You can add upto ${maxRules} rules.` -> printed "Infinity"/-1
        `You can add upto ${formatEntitlementLimit(entitlements?.max_dm_rules)} rules.`
      );
      return false;
    }

    if (keywords.length === 0) {
      toast.error('Add at least one keyword');
      return false;
    }

    // Validate based on active tab
    if (activeTab === 'zap_link') {
      const successfulLinks = zapLinks.filter(l => l.fetch_status === 'success');
      if (successfulLinks.length === 0) {
        toast.error('Add at least one valid product link in the Zap Link tab');
        return false;
      }
      /* Same all-or-nothing contract the server enforces — catch it here so a
         half-filled second button is explained rather than silently dropped. */
      const ctaLabel = zapCtaLabel.trim();
      const ctaUrl = zapCtaUrl.trim();
      if ((ctaLabel !== '') !== (ctaUrl !== '')) {
        toast.error('Add both a label and a link for the second button, or clear both');
        return false;
      }
      if (ctaUrl !== '' && !/^https?:\/\//i.test(ctaUrl)) {
        toast.error('The second button link must start with http:// or https://');
        return false;
      }
      const cta2Label = zapCta2Label.trim();
      const cta2Url = zapCta2Url.trim();
      if ((cta2Label !== '') !== (cta2Url !== '')) {
        toast.error('Add both a label and a link for the third button, or clear both');
        return false;
      }
      if (cta2Url !== '' && !/^https?:\/\//i.test(cta2Url)) {
        toast.error('The third button link must start with http:// or https://');
        return false;
      }
      // Buttons render in order — a third with no second would become the second.
      if (cta2Label !== '' && ctaLabel === '') {
        toast.error('Fill in the second button before adding a third');
        return false;
      }
    } else {
      if (commentReplies.length === 0 && !dmMessage.trim()) {
        toast.error("Add at least one Comment Reply OR a DM message");
        return false;
      }

      /* Link-button validation. The server drops a half-configured or non-http
         button and falls back to plain text, which would be a silent loss of
         what the creator typed — so catch it here where it can be explained. */
      const btnLabel = messageButtonLabel.trim();
      const btnUrl = messageButtonUrl.trim();
      if ((btnLabel !== '') !== (btnUrl !== '')) {
        toast.error('Add both a button label and a link, or clear both');
        return false;
      }
      if (btnUrl !== '' && !/^https?:\/\//i.test(btnUrl)) {
        toast.error('The button link must start with http:// or https://');
        return false;
      }
      // Meta's button template carries the button ON a text body — with no
      // message there is nothing to attach it to, and the send would fail.
      if (btnUrl !== '' && !dmMessage.trim()) {
        toast.error('Add a message for the link button to sit on');
        return false;
      }
      if (dmMessage.trim().length > dmCharLimit) {
        toast.error(`Messages with a link button are limited to ${dmCharLimit} characters`);
        return false;
      }
    }

    if (editingRuleId) {
      setRules(
        rules.map((rule) =>
          rule.ig_automation_rule_id === editingRuleId
            ? {
                ...rule,
                rule_type: activeTab,
                keywords,
                replies: commentReplies.length > 0 ? commentReplies : [],
                message_text: activeTab === 'auto_reply' ? dmMessage.trim() : '',
                scope: commentReplies.length > 1 ? 'multiple' : 'single',
                random_reply: commentReplies.length > 1 ? randomReply : false,
                status: "Live",
                is_active: true,
                zap_links: activeTab === 'zap_link' ? zapLinks : [],
                dm_flow_mode: canUseFollowGate ? dmFlowMode : 'instant',
                opener_message: openerMessage.trim(),
                opener_button_label: openerButtonLabel.trim(),
                follow_ask_message: followAskMessage.trim(),
                button_label: activeTab === 'auto_reply' ? messageButtonLabel.trim() : '',
                button_url: activeTab === 'auto_reply' ? messageButtonUrl.trim() : '',
                zap_cta_label: activeTab === 'zap_link' ? zapCtaLabel.trim() : '',
                zap_cta_url: activeTab === 'zap_link' ? zapCtaUrl.trim() : '',
                zap_cta2_label: activeTab === 'zap_link' ? zapCta2Label.trim() : '',
                zap_cta2_url: activeTab === 'zap_link' ? zapCta2Url.trim() : '',
              }
            : rule
        )
      );

      toast.success("Rule updated");
    }
    else {
      // Create new rule
      const newRule: AutomationRuleEntry = {
        new_rule: true,
        ig_automation_rule_id: rules.length + 1, // Temporary ID, should be replaced by backend ID
        rule_type: activeTab,
        keywords,
        replies: commentReplies.length > 0 ? commentReplies : [],
        message_text: activeTab === 'auto_reply' ? dmMessage.trim() : '',
        scope: commentReplies.length > 1 ? 'multiple' : 'single',
        random_reply: commentReplies.length > 1 ? randomReply : false,
        status: 'Live',
        is_active: true,
        trigger_count: 0,
        zap_links: activeTab === 'zap_link' ? zapLinks : [],
        dm_flow_mode: canUseFollowGate ? dmFlowMode : 'instant',
        opener_message: openerMessage.trim(),
        opener_button_label: openerButtonLabel.trim(),
        follow_ask_message: followAskMessage.trim(),
        button_label: activeTab === 'auto_reply' ? messageButtonLabel.trim() : '',
        button_url: activeTab === 'auto_reply' ? messageButtonUrl.trim() : '',
        zap_cta_label: activeTab === 'zap_link' ? zapCtaLabel.trim() : '',
        zap_cta_url: activeTab === 'zap_link' ? zapCtaUrl.trim() : '',
        zap_cta2_label: activeTab === 'zap_link' ? zapCta2Label.trim() : '',
        zap_cta2_url: activeTab === 'zap_link' ? zapCta2Url.trim() : '',
      };
      setRules([...rules, newRule]);
      toast.success('Rule added');
    }

    // Reset form
    setKeywords([]);
    setKeyword('');
    setCommentReplies([]);
    setCommentReply('');
    setRandomReply(true);
    setDmMessage('');
    setActiveTab('auto_reply');
    setZapLinks([]);
    // OLD: the flow fields were not reset here, so after saving a rule with an
    // opener card the NEXT rule silently inherited its mode and copy.
    setDmFlowMode('instant');
    setOpenerMessage('');
    setOpenerButtonLabel('');
    setFollowAskMessage('');
    setMessageButtonLabel('');
    setMessageButtonUrl('');
    setZapCtaLabel('');
    setZapCtaUrl('');
    setZapCta2Label('');
    setZapCta2Url('');
    setShowZapCta2(false);
    setIsAddingRule(false);
    setEditingRuleId(null);
    return true;
  };

  const handleCancelEdit = () => {
    setIsAddingRule(false);
    setEditingRuleId(null);
    setKeywords([]);
    setKeyword('');
    setCommentReplies([]);
    setCommentReply('');
    setRandomReply(true);
    setDmMessage('');
    setActiveTab('auto_reply');
    setZapLinks([]);
    // OLD: the flow fields were not reset here, so after saving a rule with an
    // opener card the NEXT rule silently inherited its mode and copy.
    setDmFlowMode('instant');
    setOpenerMessage('');
    setOpenerButtonLabel('');
    setFollowAskMessage('');
    setMessageButtonLabel('');
    setMessageButtonUrl('');
    setZapCtaLabel('');
    setZapCtaUrl('');
    setZapCta2Label('');
    setZapCta2Url('');
    setShowZapCta2(false);
  };

  /* -------------------------------------------------------------------------
     PENDING DRAFTS

     "Type, then press +" is staging: until + is pressed the text belongs to no
     rule and Create/Update Automation would drop it. Rather than warn about
     that state we simply stop producing it — the field commits itself when it
     loses focus, which is what the creator meant by typing into it. Both
     handlers below already validate, de-duplicate and enforce the plan limit,
     and they leave the text in place when they refuse, so nothing is ever lost
     silently.

     Empty is a no-op rather than an error: these run on blur and on submit, not
     only when the creator deliberately presses +, so "nothing typed" is normal
     and must stay quiet. Pressing + itself still routes through the original
     handlers, which do complain about an empty field.
     ---------------------------------------------------------------------- */
  const commitKeywordDraft = () => {
    if (!keyword.trim()) return;
    handleAddKeyword();
  };

  const commitCommentReplyDraft = () => {
    if (!commentReply.trim()) return;
    handleAddCommentReply();
  };

  /* No dependency array on purpose: the handle closes over keyword /
     commentReply / editingRuleId, and a memoised version would hand the footer
     a stale draft. Rebuilding a four-key object per render costs nothing. */
  useImperativeHandle(ref, () => ({
    flushPendingDrafts: () => {
      commitKeywordDraft();
      commitCommentReplyDraft();
    },
    hasOpenRule: () => isAddingRule || editingRuleId !== null,
    commitOpenRule: () => handleAddRule(),
    discardOpenRule: () => handleCancelEdit(),
  }));

  const handleToggleRule = (id: number | null) => {
    if (id === null) return;
    setRules(
      rules.map((rule) =>
        rule.ig_automation_rule_id === id ? { ...rule, is_active: !rule.is_active } : rule
      )
    );
  };

  const handleDeleteRule = (id: number | null) => {
    if (id === null) return;
    setRules(rules.filter((rule) => rule.ig_automation_rule_id !== id));
    toast.success('Rule removed');
  };
  useBodyClass('page-setup-wizard')
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="name">Automation Name</Label>
        <Input
          id="name"
          placeholder="e.g., Product Launch Campaign"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className='max-md:text-[14px]'
        />
        <p className="text-xs text-gray-500">
          Give your automation a descriptive name
        </p>
      </div>
      <div>
        <h3 className="mb-2">Configure Automation Rules</h3>
        <p className="text-sm text-gray-500">
          Set up keyword triggers with automated {!isStory && 'comment replies & '}DM messages
        </p>
      </div>

      {rules?.length > 0 && (
        <div className="space-y-3">
          {rules.map((rule) => (
            <Card key={rule.ig_automation_rule_id} className='border-red-500'>
              <CardContent className="rounded-md px-3 md:p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {rule.keywords.map((kw) => (
                      <Badge className='btn btn-primary' key={kw} variant={rule.is_active ? 'outline' : 'secondary'}>
                        {kw}
                      </Badge>
                    ))}
                    {rule.replies && rule.replies.length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {rule.replies.length} {rule.replies.length === 1 ? 'reply' : 'replies'}
                      </Badge>
                    )}
                    {Boolean(rule.random_reply) && rule.replies && rule.replies.length > 1 && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <Shuffle className="w-3 h-3" />
                        Random
                      </Badge>
                    )}
                    {/* What the rule actually DOES. Without these the card showed only
                        keywords and copy, so an opener card, a follow requirement and a
                        link button were all invisible until you opened the editor. */}
                    {rule.dm_flow_mode === 'button' && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <MousePointerClick className="w-3 h-3" />
                        Button first
                      </Badge>
                    )}
                    {rule.dm_flow_mode === 'soft_follow' && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <UserPlus className="w-3 h-3" />
                        Asks to follow
                      </Badge>
                    )}
                    {rule.dm_flow_mode === 'hard_follow' && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <UserPlus className="w-3 h-3" />
                        Follow required
                      </Badge>
                    )}
                    {Boolean(rule.button_url) && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <Link2 className="w-3 h-3" />
                        Link button
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Switch
                      checked={rule.is_active}
                      onCheckedChange={() => handleToggleRule(rule?.ig_automation_rule_id || null)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className='p-2'
                      onClick={() => handleEditRule(rule)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className='p-2'
                      onClick={() => handleDeleteRule(rule.ig_automation_rule_id || null)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  {/* Listed in the order the follower receives them: comment reply,
                      opener card, follow nudge, then the payload below. */}
                  {rule.dm_flow_mode && rule.dm_flow_mode !== 'instant' && (
                    <div className="flex items-start gap-2 text-sm">
                      <MousePointerClick className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <span className="text-xs text-gray-500">Opening message:</span>
                        <p className="text-gray-700 text-xs">
                          {rule.opener_message?.trim()
                            || "Hi there! Thanks for your interest. Tap below and I'll send the link right over."}
                        </p>
                        <span className="inline-block mt-1 rounded border border-gray-300 bg-gray-50 px-2 py-[2px] text-[11px] text-gray-600">
                          {rule.opener_button_label?.trim() || 'Send Link Now'}
                        </span>
                      </div>
                    </div>
                  )}

                  {(rule.dm_flow_mode === 'soft_follow' || rule.dm_flow_mode === 'hard_follow') && (
                    <div className="flex items-start gap-2 text-sm">
                      <UserPlus className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <span className="text-xs text-gray-500">If they haven&apos;t followed:</span>
                        <p className="text-gray-700 text-xs">
                          {rule.follow_ask_message?.trim()
                            || 'Thanks for your comment! Make sure to follow me so I can send you the link.'}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {rule.dm_flow_mode === 'soft_follow'
                            ? 'Sent once, then the message goes out either way.'
                            : 'The message is only sent once they actually follow.'}
                        </p>
                      </div>
                    </div>
                  )}

                  {rule.rule_type === 'zap_link' ? (
                    <div className="flex items-start gap-2 text-sm">
                      <Zap className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-gray-500">Zap Link:</span>
                        {/* OLD: just the count ("2 product links"), which told you
                            nothing about WHICH products were attached without
                            opening the editor. The first is shown expanded and the
                            rest collapse, so a 10-product carousel does not bury
                            everything else on the card. */}
                        {(() => {
                          const links = (rule.zap_links || []).filter(l => l.fetch_status === 'success');
                          if (links.length === 0) {
                            return <p className="text-gray-700 text-xs">No product links</p>;
                          }
                          const ruleKey = rule.ig_automation_rule_id ?? -1;
                          const expanded = expandedZapRules.includes(ruleKey);
                          const shown = expanded ? links : links.slice(0, 1);
                          return (
                            <div className="space-y-1.5 mt-1">
                              {shown.map((l, idx) => (
                                <div key={l.ig_zap_link_id ?? idx} className="flex items-center gap-2">
                                  {l.doc_type ? (
                                    <div className="w-8 h-8 bg-gray-50 rounded border flex items-center justify-center shrink-0">
                                      <FileText className="w-4 h-4 text-gray-400" />
                                    </div>
                                  ) : (l.manual_product_image_url || l.product_image_url) ? (
                                    <img
                                      src={l.manual_product_image_url || l.product_image_url}
                                      alt=""
                                      className="w-8 h-8 object-cover rounded border shrink-0"
                                      onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                                    />
                                  ) : (
                                    <div className="w-8 h-8 bg-gray-50 rounded border shrink-0" />
                                  )}
                                  <div className="min-w-0">
                                    <p className="text-xs text-gray-700 truncate">{l.product_name || 'Product'}</p>
                                    <p className="text-[11px] text-gray-400 truncate">
                                      {l.brand_name || l.product_url}
                                      {l.price != null && ` · ${l.currency || ''} ${l.price}`}
                                    </p>
                                  </div>
                                </div>
                              ))}
                              {links.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedZapRules(prev =>
                                    prev.includes(ruleKey) ? prev.filter(k => k !== ruleKey) : [...prev, ruleKey]
                                  )}
                                  className="text-[11px] text-blue-600 hover:text-blue-800 underline"
                                >
                                  {expanded ? 'Show less' : `+ ${links.length - 1} more product${links.length - 1 !== 1 ? 's' : ''}`}
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ) : (
                    <>
                      {rule.replies && rule.replies.length > 0 && (
                        <div className="flex items-start gap-2 text-sm">
                          <MessageSquare className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <span className="text-xs text-gray-500">Comment Replies:</span>
                            <div className="space-y-1 mt-1">
                              {rule.replies.map((reply, idx) => (
                                <p key={idx} className="text-gray-700 text-xs">• {reply}</p>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      {rule.message_text && rule.message_text.trim() !== '' && (
                        <div className="flex items-start gap-2 text-sm">
                          <Send className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="text-xs text-gray-500">DM Message:</span>
                            <p className="text-gray-700">{rule.message_text}</p>
                            {Boolean(rule.button_url) && (
                              <span
                                className="inline-block mt-1 rounded border border-gray-300 bg-gray-50 px-2 py-[2px] text-[11px] text-gray-600"
                                title={rule.button_url}
                              >
                                {rule.button_label} ↗
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {ruleError && (
        <p className="text-xs text-red-500 mt-1">{ruleError}</p>
      )}
      {!isAddingRule ? (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            if (rules.length >= maxRules) {
              // onClose();
              // handleOpenUpgradePopup(
              //   `You can add only ${maxRules} rules in your current plan.`
              // );

              // setRuleError(
              //   `You can add only ${maxRules} rules in your current plan.`
              // )

              setRuleError(
                <>
                  You can add only {maxRules === 1 ? '1 rule' : `${formatEntitlementLimit(entitlements?.max_dm_rules)} rules`} in your current plan
                  {currentPlanName === "Savings" ? (
                    "."
                  ) : (
                    <>
                      , or{" "}
                      <span
                        className="underline text-blue-600 cursor-pointer hover:text-blue-800"
                        onClick={() => navigate(zapPaths().subscription)}
                      >
                        upgrade
                      </span>{" "}
                      your plan.
                    </>
                  )}
                </>
              );
              return;
            }
            setIsAddingRule(true)
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Rule
        </Button>
      ) : (
        <div ref={formRef}>
          <Card className="border-1 border-[#dee2e6]">
            <CardContent className="p-3 space-y-4 md:p-4">
              <div className="flex items-center justify-between">
                <h4 className="flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  {editingRuleId ? 'Edit Rule' : 'New Rule'}
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="keyword" className='text-[14px] items-center'>Trigger Keywords


                  <MaterialTooltip enterTouchDelay={0}
                    leaveTouchDelay={1500}
                    disableFocusListener
                    title={
                      <p className='text-[10px]'>
                        If a user's comment contains any of these keywords (anywhere in their message), the automation will trigger
                      </p>
                    }
                    arrow
                    className=''
                  >
                    <IconButton className='p-0'>
                      <MaterialSymbol iconName='info' fontweight='200' fontSize='18px' />
                    </IconButton>
                  </MaterialTooltip>

                </Label>
                <p className='fs-12 mt-0 text-[#888]'>Add each keyword separately by tapping the + icon.</p>
                <div className="flex gap-2 max-md:gap-2 max-md:items-start">
                  <Input
                    id="keyword"
                    placeholder="e.g., INFO, DISCOUNT, LINK"
                    value={keyword}
                    onBlur={() => {
                      if (keywordError) setKeywordError(null);
                      commitKeywordDraft();
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddKeyword();
                      }
                    }}
                    onChange={(e) => {
                      setKeyword(e.target.value);
                      if (keywordError) setKeywordError(null);
                    }}
                    className='max-md:text-[12px] uppercase'
                  />
                    {/* Keeps focus in the field so pressing + does NOT fire the
                        blur-commit first. Without this the sequence is
                        mousedown -> blur -> field committed and cleared ->
                        button re-renders disabled -> the click either never
                        lands or lands on an empty field and errors. */}
                  <Button
                    type="button"
                    variant="outline"
                    className='btn btn-outline-primary'
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleAddKeyword}
                    disabled={!keyword.trim()}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {keywordError && (
                  <p className="text-xs text-red-500 mt-1">
                    {keywordError}
                  </p>
                )}
                {keywords.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {keywords.map((kw) => (
                      <Badge key={kw} variant="outline" className="flex items-center gap-1">
                        {kw}
                        <button
                          type="button"
                          onClick={() => handleRemoveKeyword(kw)}
                          className="ml-1 hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Users who comment any of these keywords will trigger the automation
                </p>
              </div>

              <hr className="border-t-2 border-gray-200" />

              {/* Comment Replies — always shown above tabs (non-story) */}
              {!isStory && (
                <div className="space-y-2">
                  {/* <Label htmlFor="commentReply">Comment Replies (Optional)</Label> */}
                  <Label htmlFor="commentReply" >
                    Comment Replies
                    <MaterialTooltip enterTouchDelay={0}
                      leaveTouchDelay={1500}
                      disableFocusListener
                      title={
                        <p className='text-[10px]'>
                          A public reply posted on the user's comment — visible to everyone on your post. Add multiple replies for variety.
                        </p>
                      }
                      arrow
                      className=''
                    >
                      <IconButton className='p-0'>
                        <MaterialSymbol iconName='info' fontweight='200' fontSize='18px' />
                      </IconButton>
                    </MaterialTooltip>

                  </Label>
                  <span className="text-gray-500 text-xs mt-0">(optional, unless DM is empty)</span>
                  <div className="flex gap-2">
                    <Textarea
                    
                      id="commentReply"
                      placeholder="e.g., Thanks for your comment! Check your DMs 💌"
                      value={commentReply}
                      onBlur={() => {
                        if (CommentRepliesError) setCommentRepliesError(null);
                        commitCommentReplyDraft();
                      }}
                      onChange={(e) => {
                        setCommentReply(e.target.value);
                        if (CommentRepliesError) setCommentRepliesError(null);
                      }}
                      rows={2}
                      className='max-md:text-[12px] '
                    />
                    {/* Same focus guard as the keyword +, see the note above. */}
                    <Button
                      type="button"
                      variant="outline"
                      className='btn btn-outline-primary self-end'
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleAddCommentReply}
                      disabled={!commentReply.trim()}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {CommentRepliesError && (
                    <p className="text-xs text-red-500 mt-1">
                      {CommentRepliesError}
                    </p>
                  )}
                  {commentReplies.length > 0 && (
                    <div className="space-y-2 mt-2 border rounded-lg p-3 bg-gray-50">
                      {commentReplies.map((reply, idx) => (
                        <div key={idx} className="flex items-start gap-2 bg-white p-2 rounded border">
                          <MessageSquare className="w-4 h-4 text-blue-500 mt-1 flex-shrink-0" />
                          <p className="text-sm flex-1">{reply}</p>
                          <button
                            type="button"
                            onClick={() => handleRemoveCommentReply(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {commentReplies.length > 1 && (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <Switch
                        id="random-reply"
                        checked={randomReply}
                        onCheckedChange={setRandomReply}
                        disabled={true}
                      />
                      <Label htmlFor="random-reply" className="flex items-center gap-2 cursor-pointer">
                        <Shuffle className="w-4 h-4 text-blue-600" />
                        <span className="text-sm">Send random reply from the list</span>
                      </Label>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Public reply to their comment. Add multiple for variety.
                  </p>
                </div>
              )}

              <hr className="border-t-2 border-gray-200" />

              {/* ---------------------------------------------------------
                  OPENING MESSAGE

                  Sits here, above the delivery tabs, because this is the order
                  the follower actually experiences: public comment reply, then
                  the opener card, then the payload. The old form put the opener
                  BELOW the message field, so the first message you typed was
                  the second one sent — the single biggest reason the flow read
                  as complicated.

                  Not shown for stories: handleStoryReplies has no postback path,
                  so an opener there would never be delivered.
                  --------------------------------------------------------- */}
              {!isStory && (
                <div className="space-y-2">
                  <div className="d-flex items-center gap-2 flex-wrap">
                    <Label htmlFor="openerToggle" className="mb-0">Send a button first</Label>
                    {!canUseFollowGate && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#f3f0ff] px-2 py-[2px] text-[11px] font-semibold leading-none text-[#7c3aed]">
                        <Lock className="h-[11px] w-[11px]" />
                        Upgrade
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                    <Switch
                      id="openerToggle"
                      checked={openerEnabled}
                      onCheckedChange={handleToggleOpener}
                    />
                    <Label htmlFor="openerToggle" className="cursor-pointer mb-0">
                      <span className="text-sm">
                        {openerEnabled
                          ? "They tap the button, then your message arrives"
                          : "Your message is sent straight away"}
                      </span>
                    </Label>
                  </div>
                  <p className="text-xs text-gray-500">
                    A tap is a reply from them, which opens Instagram's 24-hour messaging
                    window and keeps the exchange looking like a conversation.
                  </p>

                  {openerEnabled && (
                    <div className="space-y-2 border-l-2 border-gray-200 pl-3 ml-1">
                      <Label htmlFor="openerMessage" className="mb-0">Button message</Label>
                      <Textarea
                        id="openerMessage"
                        placeholder="Hi there! Thanks for your interest. Tap below and I'll send the link right over."
                        value={openerMessage}
                        onChange={(e) => setOpenerMessage(e.target.value)}
                        rows={2}
                        className='max-md:text-[12px]'
                      />
                      <SuggestionChips
                        presets={OPENER_MESSAGE_PRESETS}
                        value={openerMessage}
                        onPick={setOpenerMessage}
                      />
                      <Input
                        id="openerButtonLabel"
                        placeholder="Send Link Now"
                        value={openerButtonLabel}
                        maxLength={20}
                        onChange={(e) => setOpenerButtonLabel(e.target.value)}
                        className='max-md:text-[12px]'
                      />
                      <SuggestionChips
                        presets={OPENER_BUTTON_PRESETS}
                        value={openerButtonLabel}
                        onPick={setOpenerButtonLabel}
                      />
                      <p className="text-xs text-gray-500">
                        Button label, up to 20 characters. Leave blank for &quot;Send Link Now&quot;.
                      </p>

                      {/* The follow check is nested because it can only run on the
                          tap above — it is not a fourth independent setting. */}
                      <Label htmlFor="followMode" className="mb-0 mt-2">Ask them to follow you</Label>
                      <select
                        id="followMode"
                        className="form-select w-full rounded border border-gray-200 p-2 text-[14px] max-md:text-[12px]"
                        value={followMode}
                        onChange={(e) => handleChangeFollowMode(e.target.value as 'none' | 'soft_follow' | 'hard_follow')}
                      >
                        <option value="none">No — just send when they tap</option>
                        <option value="soft_follow">Ask once — send even if they don't</option>
                        <option value="hard_follow">Require it — only send once they follow</option>
                      </select>

                      {followMode !== 'none' && (
                        <>
                          <Textarea
                            placeholder="Thanks for your comment! Make sure to follow me so I can send you the link."
                            value={followAskMessage}
                            onChange={(e) => setFollowAskMessage(e.target.value)}
                            rows={2}
                            className='max-md:text-[12px]'
                          />
                          <SuggestionChips
                            presets={FOLLOW_ASK_PRESETS}
                            value={followAskMessage}
                            onPick={setFollowAskMessage}
                          />
                          <p className="text-xs text-gray-500">
                            Sent only if they tap without following you yet.
                          </p>
                        </>
                      )}
                      {followMode === 'hard_follow' && (
                        <p className="text-xs text-amber-600">
                          People who don't follow you never receive the message.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <hr className="border-t-2 border-gray-200" />

              <Label className="mb-0">
                {!isStory && openerEnabled ? 'Then send' : 'Send'}
              </Label>

              {/* Response Type Tabs */}
              {(() => {
                const autoReplyHasData = dmMessage.trim().length > 0;
                const zapLinkHasData = zapLinks.filter(l => l.fetch_status === 'success').length > 0;
                const autoReplyDisabled = activeTab !== 'auto_reply' && zapLinkHasData;
                const zapLinkDisabled = activeTab !== 'zap_link' && autoReplyHasData;
                return (
                  <div className="border-b flex">
                    <button
                      type="button"
                      onClick={() => !autoReplyDisabled && setActiveTab('auto_reply')}
                      disabled={autoReplyDisabled}
                      className={`px-2 md:px-4 justify-center text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'auto_reply'
                          ? 'border-blue-600 text-blue-600'
                          : autoReplyDisabled
                            ? 'border-transparent text-gray-300 cursor-not-allowed'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      title={autoReplyDisabled ? 'Clear Zap Link products to switch to Direct Message' : ''}
                    >
                      Direct Message
                      {/* <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        Send a custom private message to the user's Instagram DM — can include text, URLs, or any clickable links (e.g. Google Drive, product pages). For sharing multiple products, use Zap Link instead.
                      </TooltipContent>
                    </Tooltip> */}
                      <MaterialTooltip enterTouchDelay={0}
                        leaveTouchDelay={1500}
                        disableFocusListener
                        title={
                          <p className='text-[10px]'>
                            Send a custom private message to the user's Instagram DM — can include text, URLs, or any clickable links (e.g. Google Drive, product pages). For sharing multiple products, use Zap Link instead.
                          </p>
                        }
                        arrow
                        className=''
                      >
                        <IconButton className='p-0'>
                          <MaterialSymbol iconName='info' fontweight='200' fontSize='18px' />
                        </IconButton>
                      </MaterialTooltip>

                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (zapLinkDisabled) return;
                        if (!maxZapProducts) {
                          // onClose();
                          handleOpenUpgradePopup(
                            <>
                              Zap Link is not available on your current plan.{' '}
                              <span
                                className="underline text-blue-600 cursor-pointer hover:text-blue-800"
                                onClick={() => navigate(zapPaths().subscription)}
                              >
                                Upgrade
                              </span>{' '}
                              to unlock product link sharing via DM.
                            </>
                          );
                          return;
                        }
                        setActiveTab('zap_link');
                      }}
                      disabled={zapLinkDisabled}
                      className={`px-2 md:px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'zap_link'
                          ? 'border-yellow-500 text-yellow-600'
                          : zapLinkDisabled
                            ? 'border-transparent text-gray-300 cursor-not-allowed'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      title={zapLinkDisabled ? 'Clear Direct Message to switch to Zap Link' : ''}
                    >
                      <Zap className="w-3.5 h-3.5" />
                      Zap Link
                      <MaterialTooltip enterTouchDelay={0}
                        leaveTouchDelay={1500}
                        disableFocusListener
                        title={
                          <p className='text-[10px]'>
                            Share your products as a catalogue in the user's Instagram DM. They can browse product details and links directly in the chat.
                          </p>
                        }
                        arrow
                        className=''
                      >
                        <IconButton className='p-0'>
                          <MaterialSymbol iconName='info' fontweight='200' fontSize='18px' />
                        </IconButton>
                      </MaterialTooltip>
                      {/* <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        Share your products as a catalogue in the user's Instagram DM. They can browse product details and links directly in the chat.
                      </TooltipContent>
                    </Tooltip> */}
                    </button>
                  </div>
                );
              })()}

              {/* Zap Link Tab Content */}
              {activeTab === 'zap_link' && (
                <div className="space-y-2">
                  <Label>
                    Product Links{' '}
                    <span className="text-gray-500 text-xs">(at least one required)</span>
                  </Label>
                </div>
              )}
              {activeTab === 'zap_link' && (
                <ZapLinkSection
                  zapLinks={zapLinks}
                  setZapLinks={setZapLinks}
                  maxZapProducts={maxZapProducts}
                  handleOpenUpgradePopup={handleOpenUpgradePopup}
                  onClose={onClose}
                  currentPlanName={currentPlanName}
                  allowOtherSource={allowOtherSource}
                  productImageUploadPath={zapProductImageUploadPath}
                />
              )}

              {/* -------------------------------------------------------
                  SECOND BUTTON on every Zap Link card.

                  One setting for the whole carousel rather than one per product:
                  that is how it reads in the wild (a "Shop My Looks" beside each
                  product's own button) and it keeps this to two fields instead of
                  two per product.
                  ------------------------------------------------------- */}
              {activeTab === 'zap_link' && (
                <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                  <Label htmlFor="zapCtaLabel" className="mb-0">
                    Extra buttons
                    <span className="text-gray-500 text-xs ms-1">(optional)</span>
                  </Label>
                  <p className="text-xs text-gray-500 mt-0">
                    Shown on every card, next to the product link — a good place for your
                    storefront or link in bio. Instagram allows two on top of the product link.
                  </p>

                  {ctaSuggestions.length > 0 && (
                    <div className="flex gap-1.5 flex-wrap items-center">
                      <span className="text-[11px] text-gray-400 leading-none">Use:</span>
                      {ctaSuggestions.map((sug) => {
                        const active = zapCtaUrl.trim() === sug.url;
                        return (
                          <button
                            type="button"
                            key={sug.type}
                            title={sug.url}
                            onClick={() => {
                              if (active) { setZapCtaUrl(''); setZapCtaLabel(''); return; }
                              setZapCtaUrl(sug.url);
                              // Only fill the label if they have not written their own.
                              if (!zapCtaLabel.trim()) setZapCtaLabel(sug.label.substring(0, 20));
                            }}
                            className={`text-[11px] leading-none px-2 py-[4px] rounded-full border transition-colors ${
                              active
                                ? 'bg-[#f3f0ff] border-[#c4b5fd] text-[#7c3aed]'
                                : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {sug.label}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <Input
                    id="zapCtaLabel"
                    placeholder="Button label, e.g. Shop My Looks"
                    value={zapCtaLabel}
                    maxLength={20}
                    onChange={(e) => setZapCtaLabel(e.target.value)}
                    className='max-md:text-[12px]'
                  />
                  <Input
                    id="zapCtaUrl"
                    placeholder="https://..."
                    value={zapCtaUrl}
                    onChange={(e) => setZapCtaUrl(e.target.value)}
                    className='max-md:text-[12px]'
                  />
                  {zapCtaUrlInvalid && (
                    <p className="text-xs text-red-500">
                      The link must start with http:// or https://
                    </p>
                  )}
                  {!zapCtaUrlInvalid && (zapCtaLabel.trim() !== '') !== (zapCtaUrl.trim() !== '') && (
                    <p className="text-xs text-amber-600">
                      Fill in both the label and the link, or clear both.
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    {zapCtaLabel.length}/20 characters on the label.
                  </p>

                  {/* The third slot is revealed rather than always shown: most
                      creators want one extra button, and two empty pairs on first
                      open is exactly the clutter this section was meant to avoid. */}
                  {zapCtaComplete && !showZapCta2 && (
                    <button
                      type="button"
                      onClick={() => setShowZapCta2(true)}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      + Add another button
                    </button>
                  )}

                  {showZapCta2 && (
                    <div className="border-t pt-2 space-y-2">
                      <div className="d-flex items-center justify-between gap-2">
                        <span className="text-xs text-gray-500">Third button</span>
                        <button
                          type="button"
                          onClick={() => { setZapCta2Label(''); setZapCta2Url(''); setShowZapCta2(false); }}
                          className="text-xs text-gray-400 hover:text-red-600"
                        >
                          Remove
                        </button>
                      </div>
                      {/* Same quick-picks as the second button — a creator who put
                          their storefront on button two often wants their bio on
                          button three, and copying their own URL by hand is the
                          friction these chips exist to remove. */}
                      {ctaSuggestions.length > 0 && (
                        <div className="flex gap-1.5 flex-wrap items-center">
                          <span className="text-[11px] text-gray-400 leading-none">Use:</span>
                          {ctaSuggestions.map((sug) => {
                            const active = zapCta2Url.trim() === sug.url;
                            return (
                              <button
                                type="button"
                                key={sug.type}
                                title={sug.url}
                                onClick={() => {
                                  if (active) { setZapCta2Url(''); setZapCta2Label(''); return; }
                                  setZapCta2Url(sug.url);
                                  if (!zapCta2Label.trim()) setZapCta2Label(sug.label.substring(0, 20));
                                }}
                                className={`text-[11px] leading-none px-2 py-[4px] rounded-full border transition-colors ${
                                  active
                                    ? 'bg-[#f3f0ff] border-[#c4b5fd] text-[#7c3aed]'
                                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                {sug.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <Input
                        placeholder="Button label, e.g. Loved This"
                        value={zapCta2Label}
                        maxLength={20}
                        onChange={(e) => setZapCta2Label(e.target.value)}
                        className='max-md:text-[12px]'
                      />
                      <Input
                        placeholder="https://..."
                        value={zapCta2Url}
                        onChange={(e) => setZapCta2Url(e.target.value)}
                        className='max-md:text-[12px]'
                      />
                      {zapCta2UrlInvalid && (
                        <p className="text-xs text-red-500">
                          The link must start with http:// or https://
                        </p>
                      )}
                      {!zapCta2UrlInvalid && (zapCta2Label.trim() !== '') !== (zapCta2Url.trim() !== '') && (
                        <p className="text-xs text-amber-600">
                          Fill in both the label and the link, or clear both.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Direct Message Tab Content */}
              {activeTab === 'auto_reply' && (
                <div className="space-y-2">
                  <Label htmlFor="dmMessage">
                    Message{' '}
                    <span className="text-gray-500 text-xs">
                      {isStory ? '(required for stories)' : '(optional, unless comment reply is empty)'}
                    </span>
                  </Label>
                  <Textarea
                    id="dmMessage"
                    placeholder="Enter the private message to send..."
                    value={dmMessage}
                    onChange={(e) => setDmMessage(e.target.value)}
                    rows={3}
                    className='max-md:text-[12px]'
                  />
                  <SuggestionChips
                    presets={DM_MESSAGE_PRESETS}
                    value={dmMessage}
                    onPick={setDmMessage}
                  />
                  <p className={`text-xs ${dmMessage.length > dmCharLimit ? 'text-red-500' : 'text-gray-500'}`}>
                    {dmMessage.length}/{dmCharLimit} characters
                    {hasMessageButton && ' — Instagram\'s limit for messages with a button'}
                  </p>

                  {/* -------------------------------------------------------
                      LINK BUTTON on the delivered DM.

                      A raw URL pasted into DM text is not reliably tappable and
                      gets no preview, which is why every creator we studied
                      delivers a card with a labelled button instead. Optional —
                      leave both blank and the DM sends as plain text.
                      ------------------------------------------------------- */}
                  <div className="rounded-lg border border-gray-200 p-3 space-y-2 mt-3">
                    <div className="d-flex items-center justify-between gap-2 flex-wrap">
                      <Label htmlFor="messageButtonLabel" className="mb-0">
                        Add a link button
                        <span className="text-gray-500 text-xs ms-1">(optional)</span>
                      </Label>
                      {!canUseFollowGate && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#f3f0ff] px-2 py-[2px] text-[11px] font-semibold leading-none text-[#7c3aed]">
                          <Lock className="h-[11px] w-[11px]" />
                          Upgrade
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0">
                      Turns the DM into a tappable card. Far more clicks than a link in the text.
                    </p>
                    <div
                      onClick={() => { if (!canUseFollowGate) handleGoToUpgrade(); }}
                      className={!canUseFollowGate ? 'opacity-60 cursor-pointer space-y-2' : 'space-y-2'}
                    >
                      <Input
                        id="messageButtonLabel"
                        placeholder="Button label, e.g. Get the roadmap"
                        value={messageButtonLabel}
                        maxLength={20}
                        disabled={!canUseFollowGate}
                        onChange={(e) => setMessageButtonLabel(e.target.value)}
                        className='max-md:text-[12px]'
                      />
                      {canUseFollowGate && (
                        <SuggestionChips
                          presets={LINK_BUTTON_PRESETS}
                          value={messageButtonLabel}
                          onPick={setMessageButtonLabel}
                        />
                      )}
                      <Input
                        id="messageButtonUrl"
                        placeholder="https://..."
                        value={messageButtonUrl}
                        disabled={!canUseFollowGate}
                        onChange={(e) => setMessageButtonUrl(e.target.value)}
                        className='max-md:text-[12px]'
                      />
                    </div>
                    {canUseFollowGate && messageButtonUrlInvalid && (
                      <p className="text-xs text-red-500">
                        The link must start with http:// or https://
                      </p>
                    )}
                    {canUseFollowGate && hasMessageButton && !messageButtonUrlInvalid
                      && (messageButtonLabel.trim() === '' || messageButtonUrl.trim() === '') && (
                      <p className="text-xs text-amber-600">
                        Fill in both the label and the link, or clear both — a half-filled button sends as plain text.
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      {messageButtonLabel.length}/20 characters on the label.
                    </p>
                  </div>
                </div>
              )}
              {activeTab === 'auto_reply' && commentReplies.length === 0 && !dmMessage.trim() && (
                <p className="text-xs text-red-500">
                  {isStory ? 'You must provide at least one DM Message.' : 'You must provide at least one Comment Reply or a DM Message.'}
                </p>
              )}
              {activeTab === 'zap_link' && zapLinks.length === 0 && (
                <p className="text-xs text-red-500">
                  Add at least one product link to continue.
                </p>
              )}
              {activeTab === 'zap_link' && zapLinks.filter(l => l.fetch_status === 'success').length === 0 && zapLinks.length > 0 && (
                <p className="text-xs text-red-500">
                  At least one product link must be successfully fetched.
                </p>
              )}

              <Button
                className="w-full btn btn-primary"
                onClick={handleAddRule}
                variant="outline"
                disabled={
                  keywords.length === 0 ||
                  (activeTab === 'auto_reply' && commentReplies.length === 0 && !dmMessage.trim()) ||
                  (activeTab === 'zap_link' && zapLinks.filter(l => l.fetch_status === 'success').length === 0)
                }
              >
                {editingRuleId ? 'Update Rule' : 'Add Rule'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {rules?.length === 0 && !isAddingRule && (
        <div className="text-center py-8 text-gray-500">
          <Hash className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No rules configured yet</p>
          <p className="text-xs">Add at least one rule to continue</p>
        </div>
      )}
    </div>
  );
});
